import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

interface ImageMetadata {
  file: File;
  isPrimary: boolean;
  displayOrder: number;
}

interface MarketplaceImageUploadProps {
  images: File[];
  onImagesChange: (images: File[]) => void;
  imageMetadata: ImageMetadata[];
  onImageMetadataChange: (
    index: number,
    field: 'isPrimary' | 'displayOrder',
    value: any
  ) => void;
  maxImages?: number;
  maxSize?: number; // in MB
}

const MarketplaceImageUpload: React.FC<MarketplaceImageUploadProps> = ({
  images,
  onImagesChange,
  imageMetadata,
  onImageMetadataChange,
  maxImages = 10,
  maxSize = 5, // 5MB
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  // Function to resize image
  const resizeImage = (
    file: File,
    maxWidth: number = 1920,
    maxHeight: number = 1080,
    quality: number = 0.8
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;

        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw and resize image
        ctx?.drawImage(img, 0, 0, width, height);

        // Convert to blob and create new file
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(resizedFile);
            } else {
              reject(new Error('Failed to resize image'));
            }
          },
          file.type,
          quality
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    console.log('File upload triggered!', event.target.files);
    const files = Array.from(event.target.files || []);
    console.log('Files selected:', files);

    if (files.length === 0) return;

    setIsProcessing(true);
    const validFiles: File[] = [];

    try {
      for (const file of files) {
        // Check file type
        if (!file.type.startsWith('image/')) {
          alert(`${file.name} is not a valid image file.`);
          continue;
        }

        // Check total images limit
        if (images.length + validFiles.length >= maxImages) {
          alert(`Maximum ${maxImages} images allowed.`);
          break;
        }

        try {
          // Resize image if it's too large
          let processedFile = file;

          // Only resize if file is larger than 1MB or if we want to resize all images
          if (file.size > 1024 * 1024) {
            // 1MB threshold
            console.log(
              `Resizing ${file.name} from ${(file.size / 1024 / 1024).toFixed(2)}MB`
            );
            processedFile = await resizeImage(file);
            console.log(
              `Resized to ${(processedFile.size / 1024 / 1024).toFixed(2)}MB`
            );
          }

          // Check final file size
          if (processedFile.size > maxSize * 1024 * 1024) {
            alert(
              `${file.name} is still too large after resizing. Maximum size is ${maxSize}MB.`
            );
            continue;
          }

          validFiles.push(processedFile);
        } catch (error) {
          console.error(`Error processing ${file.name}:`, error);
          alert(
            `Failed to process ${file.name}. Please try a different image.`
          );
        }
      }

      console.log('Valid files:', validFiles);
      if (validFiles.length > 0) {
        console.log('Calling onImagesChange');
        onImagesChange([...images, ...validFiles]);
      }
    } finally {
      setIsProcessing(false);
    }

    // Reset input
    event.target.value = '';
  };

  const handlePrimaryChange = (index: number, isPrimary: boolean) => {
    if (isPrimary) {
      // If setting this image as primary, unset all others
      imageMetadata.forEach((_, i) => {
        if (i !== index) {
          onImageMetadataChange(i, 'isPrimary', false);
        }
      });
    }
    onImageMetadataChange(index, 'isPrimary', isPrimary);
  };

  const handleRemoveImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Images ({images.length}/{maxImages})
      </Typography>

      <Box sx={{ textAlign: 'center' }}>
        <input
          id="image-upload"
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileUpload}
          disabled={isProcessing}
          style={{ display: 'none' }}
        />
        <label htmlFor="image-upload">
          <Paper
            sx={{
              p: 2,
              border: '2px dashed #ccc',
              textAlign: 'center',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              opacity: isProcessing ? 0.6 : 1,
              '&:hover': {
                borderColor: isProcessing ? '#ccc' : '#FF6B35',
                backgroundColor: isProcessing
                  ? 'transparent'
                  : 'rgba(255, 107, 53, 0.04)',
              },
            }}
          >
            {isProcessing ? (
              <CircularProgress size={48} sx={{ mb: 1 }} />
            ) : (
              <CloudUploadIcon
                sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
              />
            )}
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {isProcessing ? 'Processing images...' : 'Click to upload images'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Max {maxImages} images, {maxSize}MB each (auto-resized if needed)
            </Typography>
          </Paper>
        </label>
      </Box>

      {images.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Image Settings
          </Typography>
          <Grid container spacing={2}>
            {images.map((image, index) => {
              const metadata = imageMetadata[index] || {
                isPrimary: false,
                displayOrder: index + 1,
              };
              return (
                <Grid item xs={12} md={6} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                      >
                        <ImageIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          {image.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(image.size)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveImage(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={metadata.isPrimary}
                                onChange={(e) =>
                                  handlePrimaryChange(index, e.target.checked)
                                }
                                icon={<StarBorderIcon />}
                                checkedIcon={<StarIcon />}
                              />
                            }
                            label="Primary"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            label="Order"
                            type="number"
                            value={metadata.displayOrder}
                            onChange={(e) =>
                              onImageMetadataChange(
                                index,
                                'displayOrder',
                                parseInt(e.target.value) || 1
                              )
                            }
                            inputProps={{ min: 1, max: images.length }}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default MarketplaceImageUpload;
